import type {
  CollectionReference,
  PartialWithFieldValue,
  WriteBatch,
} from 'firebase/firestore'
import {
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import {
  collectionSnapshots,
  convertDocumentSnapshotToModel,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { FirestoreSectionAssignmentConversationPromptTest } from './schema'
import { schema, writeSchema } from './schema'
import type { StreamInterface } from 'tricklejs/dist/types'
import { addDocWithError } from '../../firestore-mobx/fetch'
import { type PromptType } from '../PromptVersion/types'
import { type PostAssignmentPromptType } from '../PromptPostAssignmentType/types'
import { SectionAssignmentConversationPromptTest } from '../../models/SectionAssignmentConversationPromptTest'

const converter: FirestoreDataConverter<FirestoreSectionAssignmentConversationPromptTest> =
  {
    toFirestore: (
      data: PartialWithFieldValue<FirestoreSectionAssignmentConversationPromptTest>
    ) => {
      writeSchema.partial().parse(data)
      return data
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

const getColRef = (
  firestore: Firestore,
  {
    promptType,
    subType,
    promptVersionId,
  }: {
    promptType: PromptType.postAssignment
    subType: PostAssignmentPromptType.transcriptSearch
    promptVersionId: string
  }
): CollectionReference<FirestoreSectionAssignmentConversationPromptTest> => {
  return collection(
    firestore,
    'prompt',
    promptType,
    'type',
    subType,
    'prompt_version',
    promptVersionId,
    'test'
  ).withConverter(converter)
}

export const getPromptTestConversations = (
  repository: FirebaseRepository,
  params: Parameters<typeof getColRef>[1]
): StreamInterface<SectionAssignmentConversationPromptTest[]> => {
  const ref = getColRef(repository.firestore, params)

  const orderByCreatedAt = orderBy('createdAt', 'desc')
  const conversationsQuery = query(ref, orderByCreatedAt)

  return collectionSnapshots(conversationsQuery).map((snapshot) => {
    const models = snapshot.docs.map((doc) => {
      return convertDocumentSnapshotToModel(
        repository,
        doc,
        SectionAssignmentConversationPromptTest
      )
    })
    return models
  })
}

export const createPromptTestConversation = async (
  repository: FirebaseRepository,
  params: Parameters<typeof getColRef>[1] & {
    sectionId: string
    assignmentId: string
    userId: string
  }
): Promise<string> => {
  const ref = getColRef(repository.firestore, params)

  const data = {
    sectionId: params.sectionId,
    assignmentId: params.assignmentId,
    userId: params.userId,
    promptVersionId: params.promptVersionId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  const docRef = await addDocWithError(ref, data, 'CreateDraftAssignmentError')
  return docRef.id
}

export const batchDeletePromptTestConversation = (
  repository: FirebaseRepository,
  params: Parameters<typeof getColRef>[1] & {
    conversationId: string
    batch: WriteBatch
  }
) => {
  const docRef = doc(
    getColRef(repository.firestore, params),
    params.conversationId
  )
  return params.batch.delete(docRef)
}
